import React, { FC, useState, useEffect, memo, useCallback, useRef } from 'react';
import { TextEditor } from '@btag/bt-text-editor';
import { detectSideBySideTags } from '@btag/bt-personalization';
import { useDispatch, connect, ConnectedProps } from 'react-redux';
import { get as _get, isEmpty as _isEmpty, debounce as _debounce, isEqual as _isEqual, pull as _pull } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import CustomFonts from '../../../../../../../../componentLibrary/FontSelect/CustomFonts';
import ElementErrorSign from '../../../../../../../../componentLibrary/ElementErrorSign';
import eventService from '../../../../../../../../common/eventService';
import emitter from '../../../../../../../../common/eventService/emitter';
import { IBlock, IParagraph } from '../../../../../../../../types/content';
import * as columnItemActions from '../../../../../../../../actions/columnItem';
import * as modalActions from '../../../../../../../../actions/modals';
import { TAppState } from '../../../../../../../../reducers';
import { useFonts } from './useFonts';
import { ERR_PARAGRAPH_TOC_NO_HEADERS } from '../../../../../../../../constants/Errors';
import { APPLICATION_MODES, DRAFT_ACCESS } from '../../../../../../../../constants/itemTypes/itemTypes';
import config from '../../../../../../../../constants/configs';
import * as Styled from './styles';
import { generateElementID } from '../../../../../../../../common/generateID';
import defaultItems from '../../../../../../../../common/defaultItems';
import hasVisibleMarkup from '../../../../../../../../common/hasVisibleMarkup';
import elementTypes from '../../../../../../../../constants/elementTypes';
import { browserStorage } from '../../../../../../../../common/browserStorage';
import { setInvalidTags } from '../../../../../../../../actions/personalization';

interface IProps {
    content: IParagraph;
    blockIndex: string;
    columnIdx: string;
    elmIdx: string;
    elementIsSelected: boolean;
    elementType: string;
    multipleElementsSelected: boolean;
    reloadTextEditor: number;
    draftID: string;
    personalizationTags: {};
    isGetSingleUseTagsLoading: boolean;
    isDraftOwner: boolean;
}

const calculateLineHeight = (fontSize: any, lineHeight: any) => {
    if (Number(lineHeight)) {
        return lineHeight;
    }

    if (fontSize?.includes('px') && lineHeight?.includes('px')) {
        const size = parseFloat(fontSize);
        const height = parseFloat(lineHeight);

        const value = Math.round((height * 100) / size) / 100;

        return value.toString();
    }

    return undefined;
};

const mapStateToProps = (state: TAppState, props: IProps) => {
    const hasCustomFontsEnabled = _get(state, 'session.user.features.EMAIL_DESIGNER_CUSTOM_FONTS', false);
    const isCustomFontsLoaded = _get(state, 'customFonts.isLoaded', false);
    const customFontsData = _get(state, 'customFonts.data', {});
    const hasPersonalizationFeature = _get(state, 'session.user.features.EMAIL_DESIGNER_PERSONALIZATION', false);
    const isIntegrationsEnabled = _get(state, 'session.user.isIntegrationsEnabled', true);
    const isAdminOrParentAdmin = _get(state, 'session.user.isAdminOrParentAdmin', true);
    const integration = _get(state, 'session.integration', 'LOCALHOST');
    const isWebApp = integration === 'LOCALHOST' || integration === 'APP';
    const isWebAppOnly = isWebApp && !isIntegrationsEnabled;
    const isRemoteEditing = !!_get(state, `selectedElements.remote.${props.elmIdx}`, null);
    const reloadTextEditor = _get(state, 'reloadTextEditor', 0);
    const isVersionHistoryItemSaving = _get(state, 'versionHistory.isVersionHistoryItemSaving', false);
    const saving = _get(state, 'saving');
    const isSaving = isVersionHistoryItemSaving || saving;
    return {
        leftPanelCollapsed: _get(state, 'browser.leftPanelCollapsed', true),
        customization: state.session.user.customization,
        hasFullStylingAccess: _get(state, 'session.user.features.EMAIL_DESIGNER_STYLING_ACCESS', true),
        hasCustomFontsAccess: _get(state, 'session.user.features.EMAIL_DESIGNER_CUSTOM_FONTS', false),
        hasIntegrationCustomFontsAccess: _get(state, 'session.features.F_CUSTOM_FONTS', false),
        customizationPrimaryFont: _get(state, 'session.user.customization.primaryFont', 'Arial'),
        customizationSecondaryFont: _get(state, 'session.user.customization.secondaryFont', 'Georgia'),
        isCustomFontsLoaded,
        customFonts:
            hasCustomFontsEnabled && _get(state, 'customFonts.isInfoLoaded', false) && !_isEmpty(customFontsData)
                ? (new CustomFonts(customFontsData) as unknown as { fontID: string; value: string }[])
                : [],
        hasCustomizationFeature: _get(state, 'session.user.features.EMAIL_DESIGNER_CUSTOMIZATION', false),
        hasCustomizationAccess: isAdminOrParentAdmin && isWebApp,
        hasPersonalization: hasPersonalizationFeature && isWebAppOnly,
        F_COLOR_PALETTE: _get(state, 'session.features.F_COLOR_PALETTE', true),
        appMode: state.browser.appMode,
        isSaving,
        isWebAppOnly,
        isRemoteEditing,
        reloadTextEditor,
        personalizationTags: _get(state, 'personalization.tag.tags', {}),
        isDraftOwner: _get(state, 'session.collaboration.access', '') === DRAFT_ACCESS.OWNER,
    };
};

const connector = connect(mapStateToProps);
type TProps = IProps & ConnectedProps<typeof connector>;

const CKEditorElement: FC<TProps> = ({
    content,
    blockIndex,
    columnIdx,
    elmIdx,
    isCustomFontsLoaded,
    customFonts,
    elementIsSelected,
    multipleElementsSelected,
    elementType,
    leftPanelCollapsed,
    customization,
    hasFullStylingAccess,
    hasIntegrationCustomFontsAccess,
    customizationPrimaryFont,
    customizationSecondaryFont,
    hasCustomFontsAccess,
    hasCustomizationFeature,
    hasCustomizationAccess,
    isRemoteEditing,
    hasPersonalization,
    F_COLOR_PALETTE,
    appMode,
    isSaving,
    isWebAppOnly,
    reloadTextEditor,
    personalizationTags,
    isDraftOwner,
}) => {
    const {
        textMarkupValue,
        align,
        fontFamily,
        fontSize,
        lineHeight,
        fontID,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft,
    } = content;

    const [fonts, setFonts] = useState([]);
    /*
        new Data is for when we want to trigger a full re render in CKE
        with convertors. This should be done sparingly as CKE resets the cursor (among other things).
        This is different from the data prop that we have made to act like initData in CKE only
    */
    const [newData, setNewData] = useState(textMarkupValue);
    const [undoChange, setUndoChange] = useState(textMarkupValue);
    const [shouldTriggerUndo, setShouldTriggerUndo] = useState(false);
    const [toolbar, setToolbar] = useState({});
    const [editorPersonalizationTags, setEditorPersonalizationTags] = useState({
        directory: ['firstName', 'lastName'],
        singleUse:
            Object.keys(personalizationTags?.singleUse).filter((tag) => tag !== 'firstName' && tag !== 'lastName') ||
            [],
        handlers: {
            personalizationEdit: eventService.personalizationEdit,
            personalizationDownloadCsv: eventService.personalizationDownloadCsv,
        },
    });

    const [ckId, setCkId] = useState(generateElementID);
    const reloadTextEditorRef = useRef(reloadTextEditor);
    // latest stored markup, read by the debounced refresh which keeps the callback from its first render
    const textMarkupValueRef = useRef(textMarkupValue);
    textMarkupValueRef.current = textMarkupValue;
    const dispatch = useDispatch();

    const { fontOptions } = useFonts({
        hasFullStylingAccess,
        hasIntegrationCustomFontsAccess,
        customizationPrimaryFont,
        customizationSecondaryFont,
        hasCustomFontsAccess,
        isCustomFontsLoaded,
        customFonts,
    });

    const colorPickerProps = {
        hasCustomizationFeature,
        hasFullStylingAccess,
        hasCustomizationAccess,
        memberSiteBrandingUrl: config.memberSiteBrandingUrl,
        F_COLOR_PALETTE,
    };

    const {
        ed400TextEditorFeatures,
        cwins36CustomizationHeaders,
        ed484PersonalizationMsft,
        ed563MailMergeV2,
        feed3289FixListBulletPointStyle,
    } = useFlags();

    // eslint-disable-next-line
    const { textStyles, ...val } = customization;

    const custom = cwins36CustomizationHeaders ? customization : val;

    const generateTOC = (data: { blocks: IBlock[]; type: 'list' | 'menu' }) => {
        const { blocks, type } = data;
        let table = '';
        const canvasContainer = document.getElementById('canvasContainer');
        const headerList = canvasContainer?.querySelectorAll('h1,h2,h3');

        const { linkColor, linkDecoration } = custom; // Method only runs if these are truthy
        const decoration = (linkDecoration as string).toLowerCase(); // Value is (probably) uppercase
        let style = '';

        if (linkColor && linkDecoration) {
            style = 'text-decoration: none;';
        }

        if (headerList.length === 0) {
            eventService.notify(ERR_PARAGRAPH_TOC_NO_HEADERS, true);
            return;
        }
        blocks.forEach((block) => {
            const { columns } = block.content;
            columns.forEach((column) => {
                const { columnItems } = column;
                columnItems.forEach((item) => {
                    const elmType = item.type;
                    if (elmType === elementTypes.PARAGRAPH) {
                        const elmIndx = item.elmIdx;
                        const elmText = _get(item, 'content.textMarkupValue', '');
                        const parsedHTML = new DOMParser().parseFromString(elmText, 'text/html');
                        const headers = parsedHTML.body.querySelectorAll('h1, h2, h3');

                        headers.forEach((header) => {
                            let headerText = header.textContent?.trim();
                            // adding colour, italic, underline and bold the way ck expects it
                            if (decoration === 'underline') {
                                headerText = `<u>${headerText}</u>`;
                            }
                            if (decoration === 'italic') {
                                headerText = `<i>${headerText}</i>`;
                            }
                            if (decoration === 'bold') {
                                headerText = `<b>${headerText}</b>`;
                            }
                            if (linkColor) {
                                headerText = `<span style="color:${linkColor}">${headerText}</span>`;
                            }
                            if (headerText && headerText.length > 0) {
                                if (type === 'list') {
                                    table += `<p><a ${style} href="#${elmIndx}">${headerText}</a></p>`;
                                } else {
                                    table += `<a ${style} href="#${elmIndx}">${headerText}</a> | `;
                                }
                            }
                        });
                    }
                });
            });
        });

        table = type === 'list' ? table : table.slice(0, -2); // Remove last " | "
        const newMarkup = `${textMarkupValue}\n${table}`;
        setNewData(newMarkup);
        dispatch(
            columnItemActions.updateColumnItemContent({
                blockIndex,
                columnIdx,
                elmIdx,
                newContent: { ...content, textMarkupValue: newMarkup },
            })
        );
    };

    // does not generate an entry in the undo/redo actions
    const refreshParagraphContent = useCallback(
        (editor: any, fromEditorReady = false) => {
            const newTextMarkupValue = editor.getData();
            // An editor that (re)initializes before its data is applied (remount, watchdog restart) reports empty
            // data on ready. Storing it wipes the text with no undo entry and autosaves it, so never let a ready
            // event clear a non-empty element. Clearing through onChange (typing, source mode) is still stored.
            if (
                fromEditorReady &&
                !hasVisibleMarkup(newTextMarkupValue) &&
                hasVisibleMarkup(textMarkupValueRef.current)
            ) {
                return;
            }
            if (elementType === 'TABLE') {
                const regex = /<table/i;
                const containsTable = regex.test(newTextMarkupValue);
                if (!containsTable) {
                    const newContent = {
                        ...content,
                        textMarkupValue: defaultItems.TABLE.content.textMarkupValue,
                    };

                    dispatch(
                        columnItemActions.refreshColumnItemContent({
                            blockIndex,
                            columnIdx,
                            elmIdx,
                            newContent,
                        })
                    );
                    setNewData(defaultItems.TABLE.content.textMarkupValue);
                    setCkId(generateElementID());
                    return;
                }
            }
            if (textMarkupValue !== newTextMarkupValue || undoChange === newTextMarkupValue) {
                const newContent = {
                    ...content,
                    textMarkupValue: newTextMarkupValue,
                };

                dispatch(
                    columnItemActions.refreshColumnItemContent({
                        blockIndex,
                        columnIdx,
                        elmIdx,
                        newContent,
                    })
                );
                setNewData(newTextMarkupValue);
            }
        },
        [blockIndex, columnIdx, elmIdx, content, elementType]
    );
    // we need this on blur to set a redo/undo state like DraftJS
    const updateRedoUndo = useCallback(() => {
        // only trigger a redo/undo state if value has changed and
        // wait for saving to be done if triggered
        if (undoChange !== textMarkupValue && !isSaving) {
            dispatch(
                columnItemActions.updateColumnItemContent({
                    blockIndex,
                    columnIdx,
                    elmIdx,
                    newContent: { ...content, textMarkupValue },
                })
            );
            setUndoChange(textMarkupValue);
        } else if (undoChange !== textMarkupValue && isSaving) {
            setShouldTriggerUndo(true);
        }
    }, [blockIndex, columnIdx, elmIdx, content, textMarkupValue, undoChange, isSaving]);

    useEffect(() => {
        if (!isSaving && shouldTriggerUndo) {
            updateRedoUndo();
            setShouldTriggerUndo(false);
        }
    }, [isSaving, shouldTriggerUndo]);

    // shouldn't be too fast otherwise will have concurrence issue between onChange updating text and data receiving text
    // has to be inside useState so we can use cancel() while using debounce
    // TODO: Explore refactoring to useRef but only as long as its capable of canceling previous invocations and refreshing only the latest
    const [debouncedRefreshParagraphContent, setDebouncedRefreshParagraphContent] = useState(() =>
        _debounce(refreshParagraphContent, 1000)
    );

    // Need to update this function when padding updates from props
    // Otherwise it would overwrite padding with the initial state value when updating the content in the store
    useEffect(() => {
        setDebouncedRefreshParagraphContent(() => _debounce(refreshParagraphContent, 300));
    }, [paddingTop, paddingRight, paddingBottom, paddingLeft]);

    const debounceOnBlur = useCallback(
        (editor) => {
            _debounce(() => refreshParagraphContent(editor), 300);
        },
        [paddingTop, paddingRight, paddingBottom, paddingLeft]
    );

    useEffect(() => {
        emitter.on(`GENERATE_TABLE_OF_CONTENTS-${elmIdx}`, generateTOC);

        return () => {
            emitter.off(`GENERATE_TABLE_OF_CONTENTS-${elmIdx}`, generateTOC);
        };
    }, [textMarkupValue]);

    useEffect(() => {
        setFonts(fontOptions?.values?.fontFamily);
    }, [fontOptions]);

    // toolbar for no personalization
    const defaultOptions = [
        'fontFamily',
        'fontSize',
        'alignment',
        'lineHeight',
        'basicStyling',
        'fontColor',
        'removeFormat',
        'lists',
        'link',
        'heading',
    ];

    if (elementType !== 'TABLE') {
        defaultOptions.push('sourceEditing');
    }

    let toolbarOptions = {
        topNavOptions: defaultOptions,
        subMenuOptions: [] as string[],
    };

    const handleWindowResize = () => {
        if (ed484PersonalizationMsft && hasPersonalization) {
            // if large viewport, don't add subMenu
            if (window.innerWidth >= 1420) {
                toolbarOptions = {
                    topNavOptions: [...defaultOptions, 'personalization'],
                    subMenuOptions: [] as string[],
                };
            } else {
                // if small viewport, move options to subMenu
                toolbarOptions = {
                    topNavOptions: [..._pull(defaultOptions.slice(), 'removeFormat'), 'subMenu'],
                    subMenuOptions: ['removeFormat', 'personalization'],
                };
            }
        }
        setToolbar(toolbarOptions);
    };

    // debounce to not run handler on every pixel change
    const debouncedResizeHandler = _debounce(handleWindowResize, 100);

    useEffect(() => {
        handleWindowResize();
        window.addEventListener('resize', debouncedResizeHandler);
        return () => window.removeEventListener('resize', debouncedResizeHandler);
    }, []);

    useEffect(() => {
        // if remotely changed re render cke.
        if (isRemoteEditing) {
            setNewData(textMarkupValue);
        }
    }, [textMarkupValue, isRemoteEditing]);

    useEffect(() => {
        if (reloadTextEditor !== reloadTextEditorRef.current) {
            setNewData(textMarkupValue);
            // We want to force a full re render on restores/undos.
            setCkId(generateElementID());
            reloadTextEditorRef.current = reloadTextEditor;
        }
    }, [textMarkupValue, reloadTextEditor]);

    // delay before launching the modal, allows the user to see the tag being added before the modal pops up
    const debounceSideBySideTagsModalDispatch = useCallback(
        _debounce(
            () =>
                dispatch(
                    modalActions.setModal({
                        name: 'sideBySideTagsModal',
                        status: true,
                        location: null,
                    })
                ),
            800
        ),
        []
    );

    useEffect(() => {
        if (ed484PersonalizationMsft) {
            // This converts the textMarkupValue to plain text and checks for side-by-side tags
            const div = document.createElement('div');
            div.innerHTML = textMarkupValue;
            const result = detectSideBySideTags(div.innerText);
            const hideFutureWarnings = browserStorage.getItem('hideFutureSideBySideTagWarnings') === 'true';
            if (result.length > 0 && !hideFutureWarnings) {
                debounceSideBySideTagsModalDispatch();
            }
        }
    }, [textMarkupValue, ed484PersonalizationMsft]);

    const getPersonalizationTags = useCallback(
        () => ({
            directory: ['firstName', 'lastName'],
            singleUse:
                Object.keys(personalizationTags?.singleUse).filter(
                    (tag) => tag !== 'firstName' && tag !== 'lastName'
                ) || [],
            handlers: {
                personalizationEdit: eventService.personalizationEdit,
                personalizationDownloadCsv: eventService.personalizationDownloadCsv,
            },
        }),
        [personalizationTags]
    );

    useEffect(() => {
        setEditorPersonalizationTags(getPersonalizationTags());
    }, [personalizationTags]);

    const updateInvalidTagsList = (tags: string[]) => {
        dispatch(setInvalidTags(tags));
    };

    const editorFlagsWithMailMergeV2 = {
        ...ed400TextEditorFeatures,
        'ED-563-Mail-Merge-V2': ed563MailMergeV2,
        'feed-3289-fix-list-bullet-point-style': feed3289FixListBulletPointStyle,
    };

    return (
        <table style={{ width: '100%' }} cellSpacing="0" cellPadding="0">
            <tbody>
                <tr>
                    {
                        /*
                         * We are short-circuiting here if custom fonts are not loaded and if the element does not have fontID in the blocks data.
                         * If these two conditions pass, we check among all the custom fonts if the fontFamily and fontID that are in this element
                         * can be found. If not, either the value from the custom font was changed in the database or the fontID is not in the
                         * custom fonts array, which means that the font was unlinked from the users account.
                         */
                        isCustomFontsLoaded &&
                            fontID &&
                            !customFonts.some(({ fontID: id, value }) => id === fontID && value === fontFamily) && (
                                <ElementErrorSign
                                    dataFor="paragraph-custom-fonts-warning"
                                    tooltipMsg="Reselect your custom font in the text tool bar. If it isn’t on the list, contact your Account Manager to add it."
                                />
                            )
                    }
                    <Styled.OuterTD
                        align={align as any}
                        paddingTop={paddingTop}
                        paddingRight={paddingRight}
                        paddingBottom={paddingBottom}
                        paddingLeft={paddingLeft}
                    >
                        <Styled.InnerTable cellPadding="0" cellSpacing="0" role="presentation" onBlur={updateRedoUndo}>
                            <tbody>
                                <tr>
                                    <td
                                        align="center"
                                        valign="middle"
                                        role="presentation"
                                        style={{ textAlign: align, fontSize, lineHeight, fontFamily }}
                                    >
                                        {fonts?.length > 0 && (
                                            <TextEditor
                                                key={ckId}
                                                initData={textMarkupValue}
                                                newData={newData}
                                                fonts={fonts}
                                                isReady={fonts && fonts.length > 0}
                                                colorPickerProps={colorPickerProps}
                                                // @ts-ignore
                                                toolbarOptions={toolbar}
                                                // @ts-ignore
                                                tableSupport={elementType === 'TABLE'}
                                                isWebAppOnly={isWebAppOnly}
                                                toolbarMode="fixed"
                                                defaultStyles={{
                                                    'text-align': elementType === 'TABLE' ? 'left' : align,
                                                    'font-family': fontFamily || 'Arial',
                                                    'font-size': fontSize || '16px',
                                                    'line-height': calculateLineHeight(fontSize, lineHeight) || '1',
                                                }}
                                                toolbarHidden={
                                                    !elementIsSelected ||
                                                    multipleElementsSelected ||
                                                    appMode !== APPLICATION_MODES.EDITOR
                                                }
                                                toolbarStyles={{
                                                    left: '50%',
                                                    transform: `translateX(${leftPanelCollapsed ? '-62%' : '-50%'})`,
                                                    top: 53,
                                                    zIndex: 1,
                                                }}
                                                // @ts-ignore
                                                parentNotify={eventService.notify}
                                                editorId={elmIdx}
                                                brandStyles={custom as any}
                                                // @ts-ignore
                                                flags={editorFlagsWithMailMergeV2 as any}
                                                // @ts-ignore
                                                testIdPrefix="pikasso"
                                                personalizationTags={editorPersonalizationTags}
                                                onInvalidTagsUpdate={updateInvalidTagsList}
                                                // @ts-ignore
                                                onChange={(_, editor) => {
                                                    if (debouncedRefreshParagraphContent) {
                                                        // cancel previous invokations and refresh only the latest
                                                        debouncedRefreshParagraphContent.cancel();
                                                        debouncedRefreshParagraphContent(editor);
                                                    }
                                                }}
                                                // @ts-ignore
                                                onBlur={debounceOnBlur}
                                                onReady={(editor) => {
                                                    // once CKE is ready, it might change the textMarkupValue based on data and brandStyles sent
                                                    // and we only need to refresh the column item content if this value is different than before
                                                    debouncedRefreshParagraphContent(editor, true);
                                                }}
                                                isDraftOwner={isDraftOwner}
                                            />
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </Styled.InnerTable>
                    </Styled.OuterTD>
                </tr>
            </tbody>
        </table>
    );
};

const areEqual = (prevProps: TProps, nextProps: TProps) => {
    if (
        _isEqual(prevProps.content, nextProps.content) &&
        prevProps.elementIsSelected === nextProps.elementIsSelected &&
        prevProps.appMode === nextProps.appMode &&
        prevProps.customFonts === nextProps.customFonts &&
        prevProps.isSaving === nextProps.isSaving &&
        prevProps.personalizationTags === nextProps.personalizationTags
    ) {
        return true;
    }
    return false;
};

export default connector(memo(CKEditorElement, areEqual));
